import { Monitor, ShieldCheck } from "lucide-react";
import { useState } from "react";
import AppButton from "../../../components/ui/AppButton";
import { useToast } from "../../../hooks/useToast";
import UpdatePasswordModal from "../components/UpdatePasswordModal";
import SettingsCard from "../ui/SettingsCard";
import SettingsToggle from "../ui/SettingsToggle";
import { getDeviceLabel } from "../ui/settingsUtils";

const TWO_FA_KEY = "fleetpro.admin.twoFactorEnabled";

const SecurityContent = () => {
  const toast = useToast();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(() => {
    try {
      return localStorage.getItem(TWO_FA_KEY) === "true";
    } catch {
      return false;
    }
  });

  const handleTwoFactor = (enabled: boolean) => {
    setTwoFactorEnabled(enabled);
    localStorage.setItem(TWO_FA_KEY, String(enabled));
    toast.success({
      title: enabled ? "2FA enabled" : "2FA disabled",
      message: enabled
        ? "Two-factor authentication is now on for this account."
        : "Two-factor authentication has been turned off.",
    });
  };

  return (
    <div className="space-y-6">
      <SettingsCard
        title="Password"
        description="Use a unique password you don't reuse on other sites."
      >
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <p className="text-sm font-medium text-hColor">Account password</p>
            <p className="mt-1 text-[13px] text-pColor">
              Last changed from this workspace. Choose a long passphrase for
              the best protection.
            </p>
          </div>
          <AppButton
            type="button"
            onClick={() => setPasswordOpen(true)}
            className="h-[40px] shrink-0 px-4"
          >
            Update password
          </AppButton>
        </div>
      </SettingsCard>

      <SettingsCard
        title="Two-factor authentication"
        description="Add a second step when signing in, even if someone has your password."
        bodyClassName="px-6 py-2"
      >
        <SettingsToggle
          label="Require 2FA at sign-in"
          description="We’ll ask for a verification code in addition to your password."
          checked={twoFactorEnabled}
          onChange={handleTwoFactor}
        />
        <div
          className={`mx-0 mb-4 mt-1 flex items-start gap-3 rounded-xl px-4 py-3 ${
            twoFactorEnabled
              ? "bg-emerald-50 text-emerald-800"
              : "bg-[#F8F8F5] text-pColor"
          }`}
        >
          <ShieldCheck size={18} className="mt-0.5 shrink-0" />
          <p className="text-[13px] leading-relaxed">
            {twoFactorEnabled
              ? "Two-factor authentication is currently enabled for your account."
              : "Your account is protected by password only. Enable 2FA for stronger security."}
          </p>
        </div>
      </SettingsCard>

      <SettingsCard
        title="Active sessions"
        description="Devices that are currently signed in to FleetPro."
      >
        <div className="flex items-center justify-between gap-4 rounded-xl border border-[#EEEFEA] px-4 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Monitor size={18} />
            </span>
            <div>
              <p className="text-sm font-semibold text-hColor">
                {getDeviceLabel()}
              </p>
              <p className="text-[13px] text-pColor">
                This device · Current session
              </p>
            </div>
          </div>
          <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
            Active
          </span>
        </div>
      </SettingsCard>

      <UpdatePasswordModal
        isOpen={passwordOpen}
        onClose={() => setPasswordOpen(false)}
      />
    </div>
  );
};

export default SecurityContent;
